import {
  Alert,
  AlertFromJSON,
  BrowseInsideCollection,
  BrowseInsideCollectionFromJSON,
  CheckLendCollectionListRequest,
  CheckLendCollectionListRequestToJSON,
  CheckLendExtendRequestCollection,
  CheckLendExtendRequestCollectionToJSON,
  CheckReturnCollectionRequest,
  CheckReturnCollectionToJSON,
  DueDate,
  DueDateFromJSON,
  GetLendCollectionListRequest,
  GetLendCollectionListRequestToJSON,
  GetReturnBorrowCollectionRequest,
  GetReturnBorrowCollectionRequestToJSON,
  GetReturnCollectionRequest,
  GetReturnCollectionRequestToJSON,
  GroupListPrintRequest,
  GroupListPrintRequestToJSON,
  HTTPQuery,
  LendAppendixRequest,
  LendAppendixRequestToJSON,
  LendCollectionList,
  LendCollectionListFromJSON,
  LendExtend,
  LendExtendFromJSON,
  LendExtendRequestCollection,
  LendExtendRequestCollectionToJSON,
  LendLostRequest,
  LendLostRequestToJSON,
  LendOutRequest,
  LendOutRequestToJSON,
  LendUserInfo,
  LendUserInfoFromJSON,
  ReturnAppendixRequest,
  ReturnAppendixRequestToJSON,
  ReturnBorrowCollection,
  ReturnBorrowCollectionFromJSON,
  ReturnBorrowCollectionCheckRequest,
  ReturnBorrowCollectionCheckRequestToJSON,
  ReturnBorrowLendCollectionRequest,
  ReturnBorrowLendCollectionRequestToJSON,
  ReturnCollection,
  ReturnCollectionFromJSON,
  ReturnLendCollectionsRequest,
  ReturnLendCollectionsRequestToJSON,
  SearchBrowseInsideCollectionsRequest,
  SearchBrowseInsideCollectionsRequestToJSON,
  UpdateLendUserRemindRequest,
  UpdateLendUserRemindRequestToJSON,
  GetLendUserByIdRequest,
  GetLendUserByIdRequestToJSON,
  LendOutReceiptInfo,
  LendOutReceiptInfoFromJSON,
  ReturnCheckAlert,
  ReturnCheckAlertFromJSON
} from '@/api'
import * as runtime from '../runtime'

export interface LendExtendRequest {
  id: string
  revision: number
  collections: LendExtendRequestCollection[]
}

export interface CheckLendExtendRequest {
  id: string
  collections: CheckLendExtendRequestCollection[]
}

export interface GetLendExtendRequest {
  id: string
}

export class LendApi extends runtime.BaseAPI {
  /**
   *  貸出処理を行う
   *  貸出処理
   */
  async lendOut(request: LendOutRequest): Promise<LendOutReceiptInfo> {
    const bodyParameters: runtime.HTTPBody = LendOutRequestToJSON(request)

    const response = await this.requestPost('/lend', bodyParameters)
    return new runtime.JSONApiResponse(response, jsonValue =>
      LendOutReceiptInfoFromJSON(jsonValue)
    ).value()
  }

  /**
   *  利用者IDにより貸出利用者を取得する
   *  貸出利用者取得
   */
  async getLendUserInfoById(
    request: GetLendUserByIdRequest
  ): Promise<LendUserInfo> {
    const queryParameters: runtime.HTTPQuery = Object.fromEntries(
      Object.entries(GetLendUserByIdRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery
    const response = await this.requestGet('/lend/user', queryParameters)

    return new runtime.JSONApiResponse(response, jsonValue =>
      LendUserInfoFromJSON(jsonValue)
    ).value()
  }

  /**
   * 資料IDと利用者IDにより貸出資料一覧のチェックを行う
   * 貸出資料一覧チェック
   */
  async checkLendCollectionList(
    request: CheckLendCollectionListRequest
  ): Promise<Alert[]> {
    const bodyParameters: runtime.HTTPBody = CheckLendCollectionListRequestToJSON(
      request
    )

    const response = await this.requestPost(
      `/lend/collections/check`,
      bodyParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(AlertFromJSON)
    ).value()
  }

  /**
   * 返却予定日を取得する
   * 返却予定日取得
   */
  async getDueDates(): Promise<DueDate[]> {
    const response = await this.requestGet(`/lend/due-date`)

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(DueDateFromJSON)
    ).value()
  }

  /**
   * 資料IDと利用者IDにより今回の貸出資料一覧を取得する
   * 今回の貸出資料一覧取得
   */
  async getLendCollectionListById(
    request: GetLendCollectionListRequest
  ): Promise<LendCollectionList> {
    const bodyParameters: runtime.HTTPBody = GetLendCollectionListRequestToJSON(
      request
    )

    const response = await this.requestPost(`/lend/collection`, bodyParameters)

    return new runtime.JSONApiResponse(response, jsonValue =>
      LendCollectionListFromJSON(jsonValue)
    ).value()
  }

  /**
   *  利用者IDにより督促情報を更新する
   *  利用者督促情報更新
   */
  async remind(
    request: UpdateLendUserRemindRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: runtime.HTTPBody = UpdateLendUserRemindRequestToJSON(
      request
    )

    const response = await this.requestPost(
      `/lend/${request.id}/remind`,
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 紛失手続きを行う
   * 紛失手続き処理
   */
  async lendLost(request: LendLostRequest): Promise<void> {
    const bodyParameters: runtime.HTTPBody = LendLostRequestToJSON(request)

    const response = await this.requestPost(
      `/lend/${request.id}/lost`,
      bodyParameters
    )

    return new runtime.VoidApiResponse(response).value()
  }

  /**
   * 貸出延長資料を取得する
   * 貸出延長資料取得
   */
  async getLendExtend(request: GetLendExtendRequest): Promise<LendExtend[]> {
    const response = await this.requestGet(`/lend/${request.id}/extend`)

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(LendExtendFromJSON)
    ).value()
  }

  /**
   * 貸出延長資料のチェックを行う
   * 貸出延長資料チェック
   */
  async checkLendExtend(request: CheckLendExtendRequest): Promise<Alert[]> {
    const bodyParameters: runtime.HTTPBody = {}

    if (request.collections.length !== 0) {
      bodyParameters.collections = request.collections.map(
        CheckLendExtendRequestCollectionToJSON
      )
    }

    const response = await this.requestPost(
      `/lend/${request.id}/extend-check`,
      bodyParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(AlertFromJSON)
    ).value()
  }

  /**
   * 貸出延長を行う
   * 貸出延長
   */
  async lendExtend(request: LendExtendRequest): Promise<string> {
    const bodyParameters: runtime.HTTPBody = {
      collections: request.collections.map(LendExtendRequestCollectionToJSON),
      revision: request.revision
    }

    const response = await this.requestPost(
      `/lend/${request.id}/extend`,
      bodyParameters
    )

    return new runtime.JSONApiResponse(
      response,
      jsonValue => jsonValue.receipt_info
    ).value()
  }

  /**
   * 返却資料一覧のチェックを行う
   * 返却資料一覧チェック
   */
  async checkReturnCollections(
    request: CheckReturnCollectionRequest
  ): Promise<ReturnCheckAlert[]> {
    const bodyParameters: runtime.HTTPBody = CheckReturnCollectionToJSON(
      request
    )

    const response = await this.requestPost(
      `/lend/collections/return-check`,
      bodyParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReturnCheckAlertFromJSON)
    ).value()
  }

  /**
   * 資料を返却する
   * 資料返却
   */
  async returnLendCollections(
    request: ReturnLendCollectionsRequest
  ): Promise<ReturnCollection[]> {
    const bodyParameters: runtime.HTTPBody = ReturnLendCollectionsRequestToJSON(
      request
    )

    const response = await this.requestPost(
      `/lend/collections/return`,
      bodyParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReturnCollectionFromJSON)
    ).value()
  }

  /**
   * 借用返却する資料をチェックする
   * 資料借用返却チェック
   */
  async returnBorrowCollectionCheck(
    request: ReturnBorrowCollectionCheckRequest
  ): Promise<Alert[]> {
    const queryParameters: runtime.HTTPQuery = Object.fromEntries(
      Object.entries(ReturnBorrowCollectionCheckRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestPost(
      '/lend/collections/return-borrow-check',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(AlertFromJSON)
    ).value()
  }

  /**
   * 資料を借用返却する
   * 資料借用返却
   */
  async returnBorrowLendCollections(
    request: ReturnBorrowLendCollectionRequest
  ): Promise<ReturnBorrowCollection[]> {
    const bodyParameters: runtime.HTTPBody = ReturnBorrowLendCollectionRequestToJSON(
      request
    )

    const response = await this.requestPost(
      `/lend/collections/return-borrow`,
      bodyParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReturnBorrowCollectionFromJSON)
    ).value()
  }

  /**
   * 返却資料を取得する
   * 返却資料取得
   */
  async getReturnCollection(
    request: GetReturnCollectionRequest
  ): Promise<ReturnCollection> {
    const queryParameters: runtime.HTTPQuery = Object.fromEntries(
      Object.entries(GetReturnCollectionRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      '/lend/collections/return',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      ReturnCollectionFromJSON(jsonValue)
    ).value()
  }

  /**
   * 借用返却資料を取得する
   * 借用返却資料取得
   */
  async getReturnBorrowCollection(
    request: GetReturnBorrowCollectionRequest
  ): Promise<ReturnBorrowCollection> {
    const queryParameters: runtime.HTTPQuery = Object.fromEntries(
      Object.entries(GetReturnBorrowCollectionRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      '/lend/collections/return-borrow',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      ReturnBorrowCollectionFromJSON(jsonValue)
    ).value()
  }

  /**
   * 団体一覧表を印刷する
   */
  async groupListPrint(request: GroupListPrintRequest): Promise<void> {
    const bodyParameters: runtime.HTTPBody = GroupListPrintRequestToJSON(
      request
    )

    const response = await this.requestPost(
      `/lend/collections/group-list-print`,
      bodyParameters
    )

    return new runtime.VoidApiResponse(response).value()
  }

  /**
   * 付録を貸出する
   */
  async lendAppendix(request: LendAppendixRequest): Promise<void> {
    const bodyParameters: runtime.HTTPBody = LendAppendixRequestToJSON(request)

    const response = await this.requestPost(
      `/lend/collections/appendix-lend`,
      bodyParameters
    )

    return new runtime.VoidApiResponse(response).value()
  }

  /**
   * 付録を返却する
   */
  async returnAppendix(request: ReturnAppendixRequest): Promise<void> {
    const bodyParameters: runtime.HTTPBody = ReturnAppendixRequestToJSON(
      request
    )

    const response = await this.requestPost(
      `/lend/collections/appendix-return`,
      bodyParameters
    )

    return new runtime.VoidApiResponse(response).value()
  }

  /**
   * 館内閲覧資料を取得する
   * 館内閲覧資料取得
   */
  async searchBrowseInsideCollections(
    request: SearchBrowseInsideCollectionsRequest
  ): Promise<BrowseInsideCollection[]> {
    const queryParameters: runtime.HTTPQuery = Object.fromEntries(
      Object.entries(
        SearchBrowseInsideCollectionsRequestToJSON(request)
      ).filter(value => value[1] !== undefined)
    ) as HTTPQuery

    const response = await this.requestGet(
      `/lend/collections/browse-inside`,
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(BrowseInsideCollectionFromJSON)
    ).value()
  }
}
